import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { copy } from "../data/copy";
import { byCategory } from "../data/products";
import { ProductCard } from "./ProductCard";

const TABS = [
    { key: "veg", labelKey: "veg", color: "var(--kp-banana)" },
    { key: "nonveg", labelKey: "nonveg", color: "var(--kp-terracotta)" },
    { key: "gongura_veg", labelKey: "gongura_veg", color: "var(--kp-mango-green)" },
    { key: "gongura_nonveg", labelKey: "gongura_nonveg", color: "var(--kp-terracotta-soft)" },
    { key: "sweet", labelKey: "sweet", color: "var(--kp-mustard)" },
];

export const Products = () => {
    const { t, lang } = useLang();
    const [tab, setTab] = useState("veg");
    const list = byCategory(tab);

    return (
        <section
            id="pickles"
            className="relative py-10 md:py-32 bg-[color:var(--kp-cream)]"
            data-testid="products-section"
        >
            <div className="max-w-[1300px] mx-auto px-4 md:px-10">
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-6 md:mb-16">
                    <div>
                        <div className="eyebrow mb-2 md:mb-4">{t(copy.products.eyebrow)}</div>
                        <motion.h2
                            initial={{ opacity: 0, y: 24 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-60px" }}
                            transition={{ duration: 0.9, ease: [0.19, 1, 0.22, 1] }}
                            className={`text-3xl md:text-7xl leading-[1.02] md:leading-[0.98] font-bold tracking-editorial ${
                                lang === "te" ? "font-telugu-display" : "font-display"
                            }`}
                            data-testid="products-title"
                        >
                            {t({ en: "Every pouch,", te: "ప్రతి ప్యాకెట్," })}
                            <br />
                            <em className="italic text-[color:var(--kp-terracotta)] font-medium">
                                {t({ en: "a small heirloom.", te: "ఒక స్మృతి." })}
                            </em>
                        </motion.h2>
                    </div>
                    <Link
                        to="/shop"
                        className="btn-outline-warm shrink-0 self-start"
                        data-testid="see-all-shop"
                    >
                        {t({ en: "See all pickles", te: "అన్నీ చూడండి" })}
                        <ArrowUpRight size={14} />
                    </Link>
                </div>

                <div className="flex items-center gap-1.5 mb-6 md:mb-12 overflow-x-auto no-scrollbar -mx-4 px-4 md:mx-0 md:px-0 md:flex-wrap" data-testid="products-tabs">
                    {TABS.map((tb) => {
                        const count = byCategory(tb.key).length;
                        const active = tab === tb.key;
                        return (
                            <button
                                key={tb.key}
                                onClick={() => setTab(tb.key)}
                                className={`shrink-0 px-3.5 md:px-5 py-2 md:py-2.5 rounded-full text-[12px] md:text-sm font-bold transition-all ${
                                    active
                                        ? "bg-[color:var(--kp-ink)] text-[color:var(--kp-cream)] shadow-[0_4px_0_var(--kp-terracotta-deep)]"
                                        : "bg-[color:var(--kp-paper)] text-[color:var(--kp-ink)]/70 hover:bg-[color:var(--kp-cream-2)]"
                                }`}
                                data-testid={`tab-${tb.key}`}
                            >
                                <span className="inline-block w-2 h-2 rounded-full mr-1.5" style={{ background: tb.color }} />
                                {t(copy.products[tb.labelKey])}
                                <span className="opacity-60 ml-1 text-[10px] md:text-[11px]">({count})</span>
                            </button>
                        );
                    })}
                </div>

                <AnimatePresence mode="wait">
                    <motion.div
                        key={tab}
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        transition={{ duration: 0.45 }}
                        className="grid grid-cols-2 lg:grid-cols-3 gap-3 md:gap-8"
                    >
                        {list.map((p, i) => (
                            <ProductCard key={p.id} product={p} index={i} />
                        ))}
                    </motion.div>
                </AnimatePresence>
            </div>
        </section>
    );
};
